import { useEffect, useRef, useState, useCallback } from 'react';
import { useMemo } from 'react';
import { useTransition, animated } from 'react-spring';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { formatNumber, formatDateTime, twMerge } from '@belong/common';
import { IconQuotes } from '@belong/icons';
import { BulletList, Divider, Text, Image, Toast } from '@belong/ui';
import { UnitListingDetailsModel, UnitListingModel } from 'src/api/models';
import { ClampedText } from 'src/components/clamped-text/clamped-text';
import { type IntercomWidgetProps } from 'src/components/intercom-widget/intercom-widget';
import { useOnScreen } from 'src/features/homeowners/hooks/use-on-screen';
import { DepositTabs } from 'src/features/house-page/components/deposit-tabs/deposit-tabs';
import { HomeFeatures } from 'src/features/house-page/components/home-features/home-features';
import { HomeTour } from 'src/features/house-page/components/home-tour/home-tour';
import { InfoIcons } from 'src/features/house-page/components/info-icons/info-icons';
import { RentalExperienceCta } from 'src/features/house-page/components/rental-experience-cta/rental-experience-cta';
import { Sidebar } from 'src/features/house-page/components/sidebar/sidebar';
import { UtilitiesSection } from 'src/features/house-page/components/utilities-section/utilities-section';
import { HOUSE_PAGE_STRINGS } from 'src/features/house-page/house-page.strings';
import {
  isAvailable,
  isBlocked,
  isTourable,
} from 'src/features/listing-page/availability';
import { FavoriteButton } from 'src/features/listing-page/components/favorite-button/favorite-button';
import { ListingUnitCard } from 'src/features/listing-page/components/listing-unit-card/listing-unit-card';
import {
  getBathRoomString,
  getFormattedBeds,
} from 'src/features/listing-page/utils';
import { getNormalizedFavoritesByHomeId } from 'src/features/listing-page/utils';
import { useResidentFavorites } from 'src/features/residents';
import { useUser } from 'src/features/users/useUser';
import { useStore } from 'src/store';
import { pushModalSelector, clearModalsSelector } from 'src/store/modal';
import {
  formatAddress,
  getMediaSectionNames,
  getFeaturesDetails,
  getTag,
  getTagColor,
  isSelfTourOffered,
} from '../../utils';
import { ApplyButton } from '../apply-button/apply-button';
import { Cover } from '../cover/cover';
import { Gallery } from '../gallery/gallery';
import { InterestedPopup } from '../interested-popup/interested-popup';
import { LocalServices } from '../local-services/local-services';
import { MobileStickyBanner } from '../mobile-sticky-banner/mobile-sticky-banner';
import { RentAmount } from '../rent-amount/rent-amount';
import { ScheduleTour } from '../schedule-tour/schedule-tour';
import { StickyBanner } from '../sticky-banner/sticky-banner';
import { TagList } from '../tag-list/tag-list';
import { TourModal } from '../tour-modal/tour-modal';
import { TourSurveyModal } from '../tour-survey-modal/tour-survey-modal';

const IntercomWidget = dynamic<IntercomWidgetProps>(
  () =>
    import('src/components/intercom-widget/intercom-widget').then(
      (mod) => mod.IntercomWidget,
    ),
  { ssr: false },
);

const TOUR_REQUEST_TOAST_DURATION = 6000;

export type HousePageProps = {
  home: UnitListingDetailsModel;
  relatedUnits: UnitListingModel[];
  hasCityLpCard?: boolean;
  hasNeighborhoodLpCard?: boolean;
  isPreview?: boolean;
};

export function HousePage({
  home,
  relatedUnits,
  hasCityLpCard,
  hasNeighborhoodLpCard,
  isPreview,
}: HousePageProps) {
  const [gallerySection, setGallerySection] = useState<string | null>(null);
  const [isStickyBarVisible, setIsStickyBarVisible] = useState(false);
  const [isTourRequestToastVisible, setIsTourRequestToastVisible] =
    useState(false);
  const applyRef = useRef<HTMLAnchorElement>(null);
  const router = useRouter();
  const { favorites } = useResidentFavorites();
  const { user, isLoadingUser } = useUser();
  const pushModal = useStore(pushModalSelector);
  const closeModal = useStore(clearModalsSelector);

  const { mediaSectionNames, numberOfPhotos } = useMemo(
    () => getMediaSectionNames(home),
    [home],
  );

  const AnimatedStickyBanner = animated(StickyBanner);

  const normalizedFavoritesByHomeId = getNormalizedFavoritesByHomeId(
    favorites || [],
  );
  const isOnScreen = useOnScreen(applyRef, '-60px');
  const previousIsOnScreen = useRef(isOnScreen);

  const transitions = useTransition(isStickyBarVisible, {
    from: { y: -100 },
    enter: { y: 0 },
    leave: { y: -100 },
  });

  const {
    basicInfo,
    listingInfo,
    address,
    ownersToDisplay,
    inspectedBy,
    qualities,
    features,
    description,
    homeInterests,
  } = home;
  // `!` throughout this component: it already dereferenced `basicInfo`,
  // `address` and `features` unconditionally (see `home.address!` below).
  const { unitNumber } = basicInfo!;
  const { isDemoUnit, isAvailableForRent, isComingSoon, rentAmount, petInfo } =
    listingInfo || {};

  // Coming Soon homes have pricing and terms already set and published, even though
  // isAvailableForRent stays false until Full Listing Published — show them like any
  // other published listing rather than hiding rent/deposit info (LOVE-335).
  const shouldShowListingTerms =
    !isDemoUnit &&
    !isBlocked(listingInfo || {}) &&
    (isAvailable(listingInfo || {}) || !!isComingSoon);
  const shouldShowRentAmount = shouldShowListingTerms && !!rentAmount;

  const details = getFeaturesDetails(features!);

  const isTourEligible = isTourable({
    currentMilestone: basicInfo?.currentMilestone,
    ...listingInfo,
  });
  const isTourModalOpen =
    router.query.view === 'tours' &&
    isTourEligible &&
    (!!user?.userId || (!isLoadingUser && isSelfTourOffered(listingInfo)));
  const isSurveyModalOpen =
    router.query.view === 'survey' &&
    !!router.query.token &&
    !!router.query.attendance;
  let filteredQualities = qualities?.filter((q) => q.level === 'Good');

  if (!petInfo?.isPetFriendly) {
    filteredQualities = filteredQualities?.filter((q) => q.type !== 'Pets');
  }

  function openTourModal() {
    router.query.view = 'tours';
    router.push(router, undefined, { shallow: true });
  }

  const onTourModalClose = useCallback(() => {
    delete router.query.view;
    router.push(router, undefined, { shallow: true, scroll: false });
  }, [router]);

  function onTourClick() {
    router.query.view = 'tours';
    router.push(router, undefined, { shallow: true });
  }

  function onTourSignIn(backTo?: string) {
    pushModal({
      type: 'LOGIN',
      props: {
        onSuccessfulLogin: () => {
          closeModal();

          if (backTo) {
            router.push(backTo);
          }
        },
        shouldAskForSignUpSmsConsent: true,
      },
    });
  }

  const hasPushedTourLoginRef = useRef(false);

  useEffect(() => {
    if (
      !user?.userId &&
      !isLoadingUser &&
      !isTourModalOpen &&
      router.query.view === 'tours' &&
      isTourEligible
    ) {
      // Guard against re-pushing: this effect's deps (onTourModalClose in
      // particular) aren't guaranteed to stay referentially stable across
      // every re-render, and pushModal isn't idempotent — without this guard
      // each re-render while the tour view is open pushes another LOGIN
      // modal onto the stack, breaking dismiss/reopen (LOVE-335 bug report).
      if (!hasPushedTourLoginRef.current) {
        hasPushedTourLoginRef.current = true;
        pushModal({
          type: 'LOGIN',
          props: {
            onSuccessfulLogin: () => {
              closeModal();
            },
            signinTitle: HOUSE_PAGE_STRINGS['tour.signIn'],
            onDismiss: onTourModalClose,
            // Touring collects a phone number at sign-up, so the sign-up screen
            // needs the SMS-consent opt-in (HOM-345).
            shouldAskForSignUpSmsConsent: true,
          },
        });
      }
    } else if (hasPushedTourLoginRef.current && router.query.view !== 'tours') {
      // A sign-in closes the wall itself, after a social sign-in's contact step.
      hasPushedTourLoginRef.current = false;
      closeModal();
    }
  }, [
    user?.userId,
    closeModal,
    pushModal,
    isTourModalOpen,
    isLoadingUser,
    isTourEligible,
    onTourModalClose,
    router.query.view,
  ]);

  useEffect(() => {
    if (isOnScreen && !previousIsOnScreen.current) {
      setIsStickyBarVisible(false);
    } else if (!isOnScreen && previousIsOnScreen.current) {
      setIsStickyBarVisible(true);
    }
    previousIsOnScreen.current = isOnScreen;
  }, [isOnScreen]);

  return (
    <div className="flex w-full flex-col items-center">
      <Cover
        home={home}
        isPreview={!!isPreview}
        hasRoomsPhotos={mediaSectionNames.length > 0}
      />

      {transitions(
        (styles, isStickyBarVisible) =>
          isStickyBarVisible && (
            <AnimatedStickyBanner
              home={home}
              style={styles}
              normalizedFavoritesByHomeId={normalizedFavoritesByHomeId}
            />
          ),
      )}

      <MobileStickyBanner
        home={home}
        normalizedFavoritesByHomeId={normalizedFavoritesByHomeId}
      />

      {/* TOP CONTENT WITH SIDEBAR ON DESKTOP */}
      <div className="container mb-2xl">
        <div className="contents xl:flex xl:w-full xl:items-stretch">
          <div className="flex-1 pb-2xl">
            <div className="mb-xl">
              <Text as="h1" fontWeight="semibold">
                {formatAddress(home.address!, unitNumber)}
              </Text>
              <Text
                as="h2"
                variant="h2"
                fontWeight="semibold"
                className="mt-xs"
              >
                <BulletList
                  wrap
                  size="large"
                  items={[
                    getFormattedBeds(basicInfo),
                    getBathRoomString(basicInfo!),
                    <span key="sqft" className="flex items-center">
                      {formatNumber(basicInfo!.areaInSquareFeet!)} ft
                      <sup className="-mt-sm">2</sup>
                    </span>,
                  ]}
                />
              </Text>
            </div>
            <InfoIcons home={home} />

            {/* `?? 0` is not a behaviour change: `undefined > 1` was already
                false. The `!` inside is then safe by that same guard. */}
            {(isAvailableForRent || isComingSoon) &&
              (homeInterests?.interested ?? 0) > 1 && (
                <InterestedPopup
                  numberOfInterested={homeInterests!.interested!}
                />
              )}

            {/* Mobile only, find it inside the Sidebar on Desktop */}
            <div className="w-full xl:hidden">
              {shouldShowListingTerms && (
                <>
                  <div className="my-lg w-full">
                    <Divider />
                  </div>
                  <div className="mb-xl w-full">
                    <DepositTabs home={home} />
                  </div>

                  {home.utilities?.utilities && (
                    <>
                      <Divider />
                      <div className="w-full py-md">
                        <UtilitiesSection
                          utilities={home.utilities.utilities}
                        />
                      </div>
                    </>
                  )}

                  <div className="mb-lg w-full">
                    <Divider />
                  </div>
                  <RentalExperienceCta />
                </>
              )}
            </div>

            {description && (
              <div className="mt-2xl">
                <ClampedText description={description} />
              </div>
            )}

            {/* Owned By */}
            {(isPreview || isAvailableForRent || isComingSoon) &&
              ownersToDisplay &&
              ownersToDisplay.length > 0 && (
                <>
                  <div className="mb-sm mt-2xl">
                    <Text variant="h3" fontWeight="semibold">
                      {HOUSE_PAGE_STRINGS['ownedBy.title']}
                    </Text>
                  </div>
                  <div className="flex flex-col gap-xl">
                    {ownersToDisplay.map((owner) => (
                      <div key={owner.userId}>
                        <div className="flex items-center gap-xs">
                          <div
                            className={twMerge(
                              'flex-none border-2 border-solid rounded-full bg-green overflow-hidden relative',
                              !owner.profileImageUrl && 'border-0',
                            )}
                            style={{ width: 50, height: 50 }}
                          >
                            <Image
                              src={
                                owner.profileImageUrl ||
                                '/icons/profile_img.svg'
                              }
                              alt=""
                              className="object-cover"
                            />
                          </div>
                          <div className="flex flex-col">
                            <Text variant="body" fontWeight="semibold">
                              {owner.firstName}
                            </Text>
                            <Text variant="p1">{`${HOUSE_PAGE_STRINGS['ownedBy.belongMember']} ${formatDateTime(
                              {
                                dateTime: owner.memberSince,
                                format: 'MMM yyyy',
                              },
                            )}`}</Text>
                          </div>
                        </div>
                        {owner.aboutMe && (
                          <div className="mt-sm rounded border border-solid border-gray p-sm">
                            <div className="relative">
                              <div className="absolute" style={{ top: 0 }}>
                                <IconQuotes width={22} height={16} />
                              </div>
                              <Text className="ml-xl">{owner.aboutMe}</Text>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

            {/* Tour This Home */}
            {(isPreview || isAvailableForRent || isComingSoon) &&
              mediaSectionNames.length >= 1 && (
                <>
                  <div className="mb-sm mt-2xl">
                    <Text variant="h3" fontWeight="semibold">
                      {/* There is no `tour.title` key, so this heading has always
                          rendered empty. Suppressed rather than corrected,
                          because adding the string puts a new heading on the page. */}
                      {/* @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56 */}
                      {HOUSE_PAGE_STRINGS['tour.title']}
                    </Text>
                  </div>
                  <HomeTour
                    mediaSectionNames={mediaSectionNames}
                    numberOfPhotos={numberOfPhotos}
                    onAllPhotosClick={() =>
                      setGallerySection(mediaSectionNames[0]!.id)
                    }
                    onSectionClick={(id) => setGallerySection(id)}
                  />
                </>
              )}

            {/* Features */}
            {!!Object.keys(details?.interiorHighlights).length && (
              <>
                <div className="mb-sm mt-2xl">
                  <Text variant="h3" fontWeight="semibold">
                    {HOUSE_PAGE_STRINGS['features.title']}
                  </Text>
                </div>
                <HomeFeatures
                  home={home}
                  onFeatureClick={(category) => setGallerySection(category)}
                />
              </>
            )}

            {/* The Inspection */}
            {inspectedBy && (
              <>
                <div className="mb-sm mt-2xl">
                  <Text variant="h3" fontWeight="semibold">
                    {HOUSE_PAGE_STRINGS['inspection.title']}
                  </Text>
                </div>
                <div className="flex items-center gap-xs">
                  <div
                    className={
                      'relative flex-none overflow-hidden rounded-full border-2 border-solid bg-green'
                    }
                    style={{ width: 50, height: 50 }}
                  >
                    <Image
                      src={
                        inspectedBy.profileImageUrl || '/icons/profile_img.svg'
                      }
                      alt=""
                      className="object-cover"
                    />
                  </div>
                  <div className="flex flex-col">
                    <Text variant="body" fontWeight="semibold">
                      {inspectedBy.firstName}
                    </Text>
                    <Text variant="p1">{`${HOUSE_PAGE_STRINGS['ownedBy.belongMember']} ${formatDateTime(
                      {
                        dateTime: inspectedBy.memberSince,
                        format: 'MMM yyyy',
                      },
                    )}`}</Text>
                  </div>
                </div>
              </>
            )}
            {/* `?? 0`: `undefined > 0` was already false. */}
            {(filteredQualities?.length ?? 0) > 0 && (
              <div className="mt-sm">
                <TagList
                  list={filteredQualities!.map((quality) => ({
                    name: quality.type ?? '',
                    tag: getTag(quality.level),
                    tagColor: getTagColor(quality.level),
                  }))}
                />
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <div className="hidden xl:contents">
            <div className="mx-md bg-gray">
              <Divider direction="vertical" borderStyle="solid" />
            </div>
            <div className="flex-none">
              <Sidebar
                home={home}
                applyRef={applyRef}
                onTourHome={onTourClick}
              />
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM CONTENT */}
      <div className="container">
        {relatedUnits?.length > 0 && (
          <div className="mb-2xl">
            <div className="mb-sm">
              <Text variant="h3" fontWeight="semibold">
                {HOUSE_PAGE_STRINGS['relatedHomes.title']}
              </Text>
            </div>
            <div className="grid grid-cols-1 gap-lg md:grid-cols-2 lg:grid-cols-3">
              {relatedUnits.map((unit, idx) => (
                <ListingUnitCard
                  unit={unit}
                  key={idx}
                  fixedWidth={false}
                  favoriteButton={
                    <FavoriteButton
                      homeId={unit.basicInfo!.unitId!}
                      isFavorite={
                        !!normalizedFavoritesByHomeId[unit.basicInfo!.unitId!]
                      }
                      key={unit.basicInfo!.unitId}
                    />
                  }
                />
              ))}
            </div>
          </div>
        )}
        {(hasCityLpCard || hasNeighborhoodLpCard) && (
          <LocalServices
            address={address!}
            hasCityLpCard={!!hasCityLpCard}
            hasNeighborhoodLpCard={!!hasNeighborhoodLpCard}
          />
        )}
      </div>

      {/* FIXED BOTTOM BAR */}
      <div
        data-bottom-bar
        className="fixed bottom-0 z-10 block w-full bg-white xl:hidden"
      >
        <div className="">
          <div className="border-x-0 border-y border-solid border-gray px-2sm">
            <ScheduleTour home={home} onClick={openTourModal} />
          </div>
          <div>
            <div className="flex justify-between px-2sm py-sm">
              {shouldShowRentAmount && (
                <RentAmount rentAmount={rentAmount} priceVariant="h2" />
              )}
              <ApplyButton
                homeId={home.basicInfo?.unitId}
                listingId={home.listingInfo?.uniqueId}
                isDemoUnit={home.listingInfo?.isDemoUnit}
                isAvailableForRent={home.listingInfo?.isAvailableForRent}
                isComingSoon={home.listingInfo?.isComingSoon}
                availabilityStatus={home.listingInfo?.availabilityStatus}
                size={!shouldShowRentAmount ? 'fluid' : 'small'}
              />
            </div>
          </div>
        </div>
      </div>
      {gallerySection && (
        <Gallery
          home={home}
          mediaSections={mediaSectionNames}
          section={gallerySection}
          onDismiss={() => {
            setGallerySection(null);
          }}
        />
      )}
      {isSurveyModalOpen && (
        <TourSurveyModal
          home={home}
          isOpen={isSurveyModalOpen}
          onDismiss={() => {
            const path = new URL(router.asPath, window.location.origin)
              .pathname;
            router.push(path);
          }}
        />
      )}
      {isTourModalOpen && (
        <TourModal
          home={home}
          isOpen={isTourModalOpen}
          onDismiss={onTourModalClose}
          onTourRequestSent={() => {
            onTourModalClose();
            setIsTourRequestToastVisible(true);
          }}
          onSignIn={onTourSignIn}
        />
      )}
      <Toast
        isVisible={isTourRequestToastVisible}
        onClose={() => setIsTourRequestToastVisible(false)}
        autocloseDuration={TOUR_REQUEST_TOAST_DURATION}
      >
        {HOUSE_PAGE_STRINGS['tour.sendAvailabilitySuccessToast']}
      </Toast>
      <IntercomWidget isEnabled isStickyBarVisible />
    </div>
  );
}
